import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Clock, Users, BarChart3, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { MeetingService, Meeting } from '../../services/meeting';
import { KataCoachingService, KataCoachingSession } from '../../services/kataCoaching';
import { WorkshopService } from '../../services/workshop';
import { WorkshopDetail } from '../../types/api';
import { UserService, User } from '../../services/user';
import SearchableWorkshopDropdown, { WorkshopOption } from '../common/SearchableWorkshopDropdown';
import MeetingDetailsModal from './MeetingDetailsModal';

interface GanttPhase {
  id: string;
  name: string;
  duration?: { min: number; max: number; unit: 'days' | 'weeks' };
  type: 'phase' | 'milestone' | 'followup';
  status?: 'planned' | 'done' | 'delay';
  startDate?: Date;
  endDate?: Date;
}

interface GanttItem {
  id: string;
  phaseId: string;
  type: 'review' | 'coaching' | 'milestone' | 'followup';
  title: string;
  startDate: Date;
  endDate?: Date;
  status: 'planned' | 'done' | 'delay';
  color: string;
  details?: any;
}

type ZoomLevel = 'week' | 'month';

const WORKSHOP_PHASES: GanttPhase[] = [
  { id: 'ws-order', name: 'WS-order', type: 'phase' },
  { id: 'prepare-documents', name: 'Prepare documents (2-3 w)', duration: { min: 2, max: 3, unit: 'weeks' }, type: 'phase' },
  { id: 'validation', name: 'Validation by steering com.', type: 'milestone' },
  { id: 'prepare-intensive', name: 'Prepare intensive days (2 w)', duration: { min: 2, max: 2, unit: 'weeks' }, type: 'phase' },
  { id: 'intensive-days', name: 'Intensive days (1-3 days)', duration: { min: 1, max: 3, unit: 'days' }, type: 'phase' },
  { id: 'follow-ups', name: 'Follow-ups by steering com.', type: 'followup' },
  { id: 'pdca', name: 'PDCA activities (max 10 w)', duration: { min: 0, max: 10, unit: 'weeks' }, type: 'phase' },
  { id: 'end-of-work', name: 'End of work', type: 'milestone' },
  { id: 'closure', name: 'Closure by steering com.', type: 'milestone' },
];

const QUESTIONS = [
  "What is the Target Condition? / Quel est Votre Cible?",
  "What was your last step? / Quelle est la situation actuelle?",
  "What actually happened? / Que s'est il passé factuellement?",
  "What did you learn? / Qu'avez-vous appris?",
  "What obstacles are now preventing you from reaching the target condition? / Quels sont selon vous les obstacles qui vous empechent d'atteindre votre objectif?",
  "Which obstacle are you addressing now? Why? / Lequel de ces obstacles allez vous considérer? Pourquoi?",
  "What is the cause of this obstacle? / Quelle est la cause de cet obstacle?",
  "What is your next step? (next PDCA/ Experiment) / Quelle est donc votre prochaine étape? PDCA Experimentation? Gemba sur process",
  "What do you expect? / Quelle sont vos attentes?",
  "When can we go and see what we have learned from taking that step? / Quand est ce qu'on peux se rencontrer pour verifier ce que vous avez appris?"
];

const GanttTab: React.FC = () => {
  const [selectedWorkshop, setSelectedWorkshop] = useState<number | null>(null);
  const [workshops, setWorkshops] = useState<WorkshopOption[]>([]);
  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [coachingSessions, setCoachingSessions] = useState<KataCoachingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedCoachingSession, setSelectedCoachingSession] = useState<KataCoachingSession | null>(null);
  const [isCoachingModalOpen, setIsCoachingModalOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState<{ itemId: string; phaseId: string } | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [mentorName, setMentorName] = useState<string>('');
  const [menteeNames, setMenteeNames] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<{ item: GanttItem; x: number; y: number } | null>(null);
  const ganttRef = useRef<HTMLDivElement>(null);

  // Calculate year start for weeks 1-52
  const yearStartDate = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const start = new Date(currentYear, 0, 1);
    start.setDate(start.getDate() - start.getDay()); // Start of first week
    return start;
  }, []);

  const yearEndDate = useMemo(() => {
    const end = new Date(yearStartDate);
    end.setDate(end.getDate() + 52 * 7);
    return end;
  }, [yearStartDate]);

  const [dateRange] = useState<{ start: Date; end: Date }>(() => {
    const currentYear = new Date().getFullYear();
    const start = new Date(currentYear, 0, 1);
    start.setDate(start.getDate() - start.getDay()); // Start of first week
    const end = new Date(start);
    end.setDate(end.getDate() + 52 * 7);
    return { start, end };
  });

  // Fetch workshops and users
  useEffect(() => {
    const loadData = async () => {
      try {
        const [workshopsResponse, usersResponse] = await Promise.all([
          WorkshopService.getWorkshops({ page_size: 1000 }),
          UserService.getAllDirectoryUsers()
        ]);
        
        const workshopOptions: WorkshopOption[] = workshopsResponse.results.map((w: any) => ({
          id: w.id,
          title: w.title,
          department_name: w.department_name || 'N/A'
        }));
        setWorkshops(workshopOptions);
        setUsers(usersResponse);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Fetch selected workshop details and resolve mentor/mentee names
  useEffect(() => {
    const loadWorkshopDetails = async () => {
      if (!selectedWorkshop) {
        setWorkshop(null);
        setMentorName('');
        setMenteeNames([]);
        return;
      }
      try {
        const response = await WorkshopService.getWorkshop(selectedWorkshop);
        setWorkshop(response);

        // Resolve mentor name (can be ID or string)
        if (response.mentor) {
          const mentorId = typeof response.mentor === 'number' ? response.mentor : parseInt(response.mentor);
          if (!isNaN(mentorId)) {
            const user = users.find(u => u.id === mentorId);
            setMentorName(user ? user.full_name : mentorId.toString());
          } else {
            setMentorName(response.mentor);
          }
        } else {
          setMentorName('N/A');
        }

        // Resolve mentee names (can be array of IDs or strings)
        if (response.mentees && Array.isArray(response.mentees) && response.mentees.length > 0) {
          const resolvedNames = response.mentees.map((mentee: any) => {
            if (typeof mentee === 'number') {
              const user = users.find(u => u.id === mentee);
              return user ? user.full_name : mentee.toString();
            } else if (typeof mentee === 'string') {
              const menteeId = parseInt(mentee);
              if (!isNaN(menteeId)) {
                const user = users.find(u => u.id === menteeId);
                return user ? user.full_name : mentee;
              }
              return mentee;
            }
            return mentee.toString();
          });
          setMenteeNames(resolvedNames);
        } else {
          setMenteeNames([]);
        }
      } catch (error) {
        console.error('Error loading workshop details:', error);
      }
    };
    loadWorkshopDetails();
  }, [selectedWorkshop, users]);

  // Fetch meetings and coaching sessions
  useEffect(() => {
    const loadData = async () => {
      if (!selectedWorkshop) {
        setMeetings([]);
        setCoachingSessions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch meetings for selected workshop
        const meetingsData = await MeetingService.getMeetings({
          start_date: dateRange.start.toISOString().split('T')[0],
          end_date: dateRange.end.toISOString().split('T')[0],
          workshop: selectedWorkshop
        });
        setMeetings(meetingsData);

        // Fetch coaching sessions for selected workshop
        const coachingResponse = await KataCoachingService.getSessions({
          workshop: selectedWorkshop,
          coaching_date_from: dateRange.start.toISOString().split('T')[0],
          coaching_date_to: dateRange.end.toISOString().split('T')[0],
          page_size: 1000
        });
        setCoachingSessions(coachingResponse.results || []);
      } catch (error) {
        console.error('Error loading Gantt data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedWorkshop, dateRange]);

  // Convert data to Gantt items
  const ganttItems = useMemo(() => {
    if (!workshop || !selectedWorkshop) return [];

    const items: GanttItem[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add workshop reviews (meetings)
    meetings.forEach(meeting => {
      const meetingDate = new Date(`${meeting.date}T${meeting.time}`);
      const endDate = new Date(meetingDate);
      endDate.setMinutes(endDate.getMinutes() + meeting.duration);

      let status: 'planned' | 'done' | 'delay' = 'planned';
      if (meeting.status === 'completed') {
        status = 'done';
      } else if (meeting.date < today.toISOString().split('T')[0]) {
        status = 'delay';
      }

      items.push({
        id: `meeting-${meeting.id}`,
        phaseId: 'follow-ups',
        type: 'review',
        title: `Review: ${meeting.title}`,
        startDate: meetingDate,
        endDate: endDate,
        status,
        color: status === 'done' ? '#10B981' : status === 'delay' ? '#EF4444' : '#FFFFFF',
        details: meeting
      });
    });

    // Add coaching sessions
    coachingSessions.forEach(session => {
      if (session.coaching_date) {
        const coachingDate = new Date(session.coaching_date);
        const endDate = new Date(coachingDate);
        endDate.setHours(endDate.getHours() + 1);

        let status: 'planned' | 'done' | 'delay' = 'planned';
        if (coachingDate < today) {
          status = 'done';
        }

        items.push({
          id: `coaching-${session.id}`,
          phaseId: 'intensive-days',
          type: 'coaching',
          title: 'Coaching Session',
          startDate: coachingDate,
          endDate: endDate,
          status,
          color: '#10B981',
          details: session
        });
      }
    });

    // Add milestones based on workshop dates
    if (workshop.startup_date) {
      const startupDate = new Date(workshop.startup_date);
      items.push({
        id: `milestone-startup`,
        phaseId: 'validation',
        type: 'milestone',
        title: 'Workshop Start',
        startDate: startupDate,
        status: startupDate < today ? 'done' : 'planned',
        color: '#FBBF24',
        details: { type: 'startup', workshop }
      });
    }

    if (workshop.deadline) {
      const deadlineDate = new Date(workshop.deadline);
      items.push({
        id: `milestone-deadline`,
        phaseId: 'end-of-work',
        type: 'milestone',
        title: 'Workshop Deadline',
        startDate: deadlineDate,
        status: deadlineDate < today ? 'done' : 'planned',
        color: '#FBBF24',
        details: { type: 'deadline', workshop }
      });
    }

    return items.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [workshop, meetings, coachingSessions]);

  // Helper function to get the year start (Monday of week containing Jan 1)
  const getYearStart = (year: number): Date => {
    const jan1 = new Date(year, 0, 1);
    const jan1Day = jan1.getDay();
    const daysToMonday = jan1Day === 0 ? -6 : 1 - jan1Day;
    const yearStart = new Date(jan1);
    yearStart.setDate(yearStart.getDate() + daysToMonday);
    yearStart.setHours(0, 0, 0, 0);
    return yearStart;
  };

  // Helper function to get week number of year (1-52) using ISO 8601 standard
  const getWeekNumber = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    
    // Find the Monday of the week containing the date (ISO week starts on Monday)
    const dayOfWeek = d.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + daysToMonday);
    
    // Get year start (Monday of week containing Jan 1)
    const yearStart = getYearStart(d.getFullYear());
    
    // Calculate week number
    const daysDiff = Math.floor((d.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysDiff / 7) + 1;
    
    // Clamp to valid range (1-52)
    return Math.max(1, Math.min(52, weekNumber));
  };

  // Calculate timeline dimensions
  const timelineConfig = useMemo(() => {
    // Calculate weeks 1-52 based on current year
    const currentYear = new Date().getFullYear();
    const yearStart = getYearStart(currentYear);
    
    let dayWidth = 4;
    if (zoomLevel === 'week') {
      dayWidth = 16;
    } else { // month
      dayWidth = 4;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentWeekNumber = getWeekNumber(today);

    // Generate week labels (1-52)
    const weekLabels: { label: string; weekNumber: number; date: Date; position: number }[] = [];
    
    for (let weekNum = 1; weekNum <= 52; weekNum++) {
      const weekStart = new Date(yearStart);
      weekStart.setDate(weekStart.getDate() + (weekNum - 1) * 7);
      
      const daysFromYearStart = Math.floor((weekStart.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
      weekLabels.push({
        label: weekNum.toString(),
        weekNumber: weekNum,
        date: weekStart,
        position: daysFromYearStart * dayWidth
      });
    }

    const yearEnd = new Date(yearStart);
    yearEnd.setDate(yearEnd.getDate() + 52 * 7);

    return {
      yearStart,
      yearEnd,
      dayWidth,
      today,
      currentWeekNumber,
      weekLabels,
      getDatePosition: (date: Date) => {
        const weekNum = getWeekNumber(date);
        const weekStart = new Date(yearStart);
        weekStart.setDate(weekStart.getDate() + (weekNum - 1) * 7);
        const daysFromYearStart = Math.floor((weekStart.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        // Position at the start of the week + half week for better visibility
        return daysFromYearStart * dayWidth;
      },
      getWeekPosition: (weekNumber: number) => {
        if (weekNumber < 1 || weekNumber > 52) return 0;
        const weekStart = new Date(yearStart);
        weekStart.setDate(weekStart.getDate() + (weekNumber - 1) * 7);
        const daysFromYearStart = Math.floor((weekStart.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        return daysFromYearStart * dayWidth;
      }
    };
  }, [zoomLevel]);

  // Handle item click
  const handleItemClick = async (item: GanttItem) => {
    if (item.type === 'review' && item.details) {
      setSelectedMeeting(item.details as Meeting);
      setIsMeetingModalOpen(true);
    } else if (item.type === 'coaching' && item.details) {
      const session = item.details as KataCoachingSession;
      console.log('Selected coaching session:', session);
      
      // If the session doesn't have all details, fetch them
      if (session.id && (!session.question_1_score && session.question_1_score !== 0)) {
        try {
          const fullSession = await KataCoachingService.getSession(session.id);
          console.log('Fetched full coaching session:', fullSession);
          setSelectedCoachingSession(fullSession);
        } catch (error) {
          console.error('Failed to fetch full coaching session:', error);
          setSelectedCoachingSession(session);
        }
      } else {
        setSelectedCoachingSession(session);
      }
      setIsCoachingModalOpen(true);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, itemId: string, phaseId: string) => {
    setDraggedItem({ itemId, phaseId });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, targetDate: Date, targetPhaseId: string) => {
    e.preventDefault();
    if (!draggedItem) return;

    // TODO: Implement actual API call to update meeting/coaching date
    console.log('Dropped item:', draggedItem, 'to date:', targetDate, 'phase:', targetPhaseId);
    setDraggedItem(null);
  };

  const handleZoomChange = (newZoom: ZoomLevel) => {
    setZoomLevel(newZoom);
  };

  if (loading && !selectedWorkshop) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Workshop Filter and Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Workshop Macro Planning</h2>
            <p className="text-sm text-gray-600 mt-1">
              Visualize workshop phases, reviews, and coaching sessions
            </p>
          </div>
        </div>

        {/* Workshop Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Workshop
          </label>
          <SearchableWorkshopDropdown
            workshops={workshops}
            selectedWorkshops={selectedWorkshop ? [selectedWorkshop] : []}
            onSelectionChange={(ids) => setSelectedWorkshop(ids.length > 0 ? ids[0] : null)}
            placeholder="Select a workshop..."
            multiple={false}
            className="w-full"
          />
        </div>

        {/* Zoom Controls */}
        {selectedWorkshop && (
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2 border rounded-md p-1">
              <button
                onClick={() => handleZoomChange('week')}
                className={`px-3 py-1 text-sm rounded ${zoomLevel === 'week' ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Week
              </button>
              <button
                onClick={() => handleZoomChange('month')}
                className={`px-3 py-1 text-sm rounded ${zoomLevel === 'month' ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Month
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Workshop Info with Start Date, End Date, Progress, Mentor and Mentees */}
      {workshop && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-gray-600 font-medium">Start Date:</span>
              <span className="ml-2">{workshop.startup_date ? new Date(workshop.startup_date).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">End Date:</span>
              <span className="ml-2">{workshop.deadline ? new Date(workshop.deadline).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Progress:</span>
              <span className="ml-2 font-medium">
                {workshop.progress_rate ? parseFloat(workshop.progress_rate.toString()).toFixed(2) : '0.00'}%
              </span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Mentor:</span>
              <span className="ml-2">{mentorName || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Mentees:</span>
              <span className="ml-2">
                {menteeNames.length > 0 ? menteeNames.join(', ') : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Gantt Chart */}
      {selectedWorkshop ? (
        <div className="bg-white rounded-lg shadow overflow-hidden" ref={ganttRef}>
          {/* Legend */}
          <div className="border-b border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-900 bg-white"></div>
                <span>Planned</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500"></div>
                <span>Done</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-900 bg-red-500"></div>
                <span>Delay</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 transform rotate-45 bg-yellow-400 border border-gray-900" style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}></div>
                <span>Milestone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-blue-500"></div>
                <span>Follow-up</span>
              </div>
            </div>
          </div>

          {/* Gantt Chart Content */}
          <div className="p-6 overflow-x-auto">
            {ganttItems.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No reviews or coaching sessions found for this workshop</p>
              </div>
            ) : (
              <div className="relative" style={{ minWidth: `${52 * 7 * timelineConfig.dayWidth}px`, width: `${52 * 7 * timelineConfig.dayWidth}px` }}>
                {/* Week Labels Header */}
                <div className="sticky top-0 bg-white z-10 mb-4">
                  <div className="flex items-center">
                    <div className="w-48 flex-shrink-0 font-semibold text-sm text-gray-700">Week</div>
                    <div 
                      className="relative border-b border-gray-300" 
                      style={{ 
                        height: '30px',
                        width: `${52 * 7 * timelineConfig.dayWidth}px`,
                        minWidth: `${52 * 7 * timelineConfig.dayWidth}px`
                      }}
                    >
                      {timelineConfig.weekLabels.map((week, index) => (
                        <div
                          key={index}
                          className="absolute text-xs text-gray-600"
                          style={{ left: `${week.position}px`, top: '8px' }}
                        >
                          {week.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              {/* Current Date Indicator - positioned at current week number */}
              {timelineConfig.currentWeekNumber >= 1 && timelineConfig.currentWeekNumber <= 52 && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
                  style={{
                    left: `${timelineConfig.getWeekPosition(timelineConfig.currentWeekNumber) + 248}px`,
                    height: `${WORKSHOP_PHASES.length * 50 + 50}px`
                  }}
                >
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    Today (Week {timelineConfig.currentWeekNumber})
                  </div>
                </div>
              )}

                {/* Phase Rows */}
                <div className="space-y-1">
                  {WORKSHOP_PHASES.map((phase) => {
                    const phaseItems = ganttItems.filter(item => item.phaseId === phase.id);
                    
                    return (
                      <div key={phase.id} className="flex items-center gap-4 min-h-[40px]">
                        {/* Phase Label */}
                        <div className="w-48 text-sm text-gray-700 font-medium truncate flex-shrink-0">
                          {phase.name}
                        </div>

                        {/* Timeline Area */}
                        <div 
                          className="flex-1 relative border-b border-gray-200" 
                          style={{ 
                            height: '40px',
                            width: `${52 * 7 * timelineConfig.dayWidth}px`,
                            minWidth: `${52 * 7 * timelineConfig.dayWidth}px`
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const days = Math.floor(x / timelineConfig.dayWidth);
                            const targetDate = new Date(dateRange.start);
                            targetDate.setDate(targetDate.getDate() + days);
                            handleDrop(e, targetDate, phase.id);
                          }}
                        >
                          {phaseItems.map((item) => {
                            const left = timelineConfig.getDatePosition(item.startDate);
                            const width = item.endDate 
                              ? Math.max(4, timelineConfig.getDatePosition(item.endDate) - left)
                              : 4;

                            let visualElement: React.ReactNode;

                            if (item.type === 'milestone') {
                              // Yellow diamond for milestones
                              visualElement = (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, item.id, phase.id)}
                                  onClick={() => handleItemClick(item)}
                                  className="absolute cursor-move hover:opacity-80"
                                  style={{
                                    left: `${left - 8}px`,
                                    top: '8px',
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: item.color,
                                    border: '2px solid #000',
                                    clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                                  }}
                                  title={item.title}
                                />
                              );
                            } else if (item.type === 'coaching') {
                              // Triangle for coaching sessions
                              const triangleColor = item.status === 'done' ? '#10B981' : 
                                                   item.status === 'delay' ? '#EF4444' : '#60A5FA';
                              
                              visualElement = (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, item.id, phase.id)}
                                  onClick={() => handleItemClick(item)}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHoveredItem({ item, x: rect.left, y: rect.top });
                                  }}
                                  onMouseLeave={() => setHoveredItem(null)}
                                  className="absolute cursor-pointer hover:opacity-80"
                                  style={{
                                    left: `${left - 8}px`,
                                    top: '8px',
                                    width: '0',
                                    height: '0',
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderBottom: `16px solid ${triangleColor}`
                                  }}
                                />
                              );
                            } else if (item.type === 'review') {
                              // Square for planned reviews (meetings)
                              const squareColor = item.status === 'done' ? '#10B981' : 
                                                 item.status === 'delay' ? '#EF4444' : '#60A5FA';
                              
                              visualElement = (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, item.id, phase.id)}
                                  onClick={() => handleItemClick(item)}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHoveredItem({ item, x: rect.left, y: rect.top });
                                  }}
                                  onMouseLeave={() => setHoveredItem(null)}
                                  className="absolute cursor-pointer hover:opacity-80"
                                  style={{
                                    left: `${left - 8}px`,
                                    top: '8px',
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: squareColor,
                                    border: '2px solid #1F2937'
                                  }}
                                />
                              );
                            } else if (item.type === 'followup' || phase.type === 'followup') {
                              // Blue triangle for follow-ups
                              visualElement = (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, item.id, phase.id)}
                                  onClick={() => handleItemClick(item)}
                                  className="absolute cursor-move hover:opacity-80"
                                  style={{
                                    left: `${left - 8}px`,
                                    top: '0px',
                                    width: '0',
                                    height: '0',
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderTop: '16px solid #3B82F6'
                                  }}
                                  title={item.title}
                                />
                              );
                            } else {
                              // Rectangle for phases (planned/done/delay)
                              visualElement = (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, item.id, phase.id)}
                                  onClick={() => handleItemClick(item)}
                                  className={`absolute rounded cursor-move hover:opacity-80 flex items-center px-2 ${
                                    item.status === 'done' ? 'bg-green-500' :
                                    item.status === 'delay' ? 'bg-red-500 border-2 border-gray-900' :
                                    'bg-white border-2 border-gray-900'
                                  }`}
                                  style={{
                                    left: `${left}px`,
                                    width: `${width}px`,
                                    top: '8px',
                                    height: '24px'
                                  }}
                                  title={`${item.title} - ${item.startDate.toLocaleDateString()}`}
                                >
                                  <span className="text-xs text-gray-900 font-medium truncate">
                                    {item.type === 'review' ? 'Review' : 'Coaching'}
                                  </span>
                                </div>
                              );
                            }

                            return <React.Fragment key={item.id}>{visualElement}</React.Fragment>;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BarChart3 className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            Select a Workshop
          </h3>
          <p className="text-gray-600">
            Please select a workshop from the dropdown above to view its Gantt chart
          </p>
        </div>
      )}

      {/* Meeting Details Modal */}
      {selectedMeeting && (
        <MeetingDetailsModal
          isOpen={isMeetingModalOpen}
          onClose={() => {
            setIsMeetingModalOpen(false);
            setSelectedMeeting(null);
          }}
          meeting={selectedMeeting}
        />
      )}

      {/* Custom Tooltip */}
      {hoveredItem && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${hoveredItem.x}px`,
            top: `${hoveredItem.y - 10}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 max-w-xs">
            {hoveredItem.item.type === 'coaching' ? (
              <>
                <div className="font-semibold mb-2 text-blue-300">Coaching Session</div>
                <div className="space-y-1">
                  <div><span className="text-gray-400">Date:</span> {(hoveredItem.item.details as KataCoachingSession).coaching_date ? new Date((hoveredItem.item.details as KataCoachingSession).coaching_date).toLocaleDateString() : 'Not scheduled'}</div>
                  <div><span className="text-gray-400">Status:</span> {hoveredItem.item.status === 'done' ? 'Completed' : hoveredItem.item.status === 'delay' ? 'Delayed' : 'Planned'}</div>
                  <div><span className="text-gray-400">Score:</span> {(hoveredItem.item.details as KataCoachingSession).total_score || 0}/50</div>
                  <div><span className="text-gray-400">Duration:</span> {(hoveredItem.item.details as KataCoachingSession).coaching_lead_time_minutes ? `${(hoveredItem.item.details as KataCoachingSession).coaching_lead_time_minutes} min` : 'Not specified'}</div>
                </div>
              </>
            ) : hoveredItem.item.type === 'review' ? (
              <>
                <div className="font-semibold mb-2 text-blue-300">{(hoveredItem.item.details as Meeting).title}</div>
                <div className="space-y-1">
                  <div><span className="text-gray-400">Date:</span> {new Date((hoveredItem.item.details as Meeting).date).toLocaleDateString()}</div>
                  <div><span className="text-gray-400">Time:</span> {(hoveredItem.item.details as Meeting).time}</div>
                  <div><span className="text-gray-400">Duration:</span> {(hoveredItem.item.details as Meeting).duration} min</div>
                  <div><span className="text-gray-400">Location:</span> {(hoveredItem.item.details as Meeting).location || 'Not specified'}</div>
                  <div><span className="text-gray-400">Attendees:</span> {(hoveredItem.item.details as Meeting).attendee_count || 0}</div>
                  <div><span className="text-gray-400">Status:</span> {(hoveredItem.item.details as Meeting).status || 'Scheduled'}</div>
                </div>
              </>
            ) : null}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}

      {/* Coaching Session Modal */}
      {isCoachingModalOpen && selectedCoachingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-0 border-0 w-11/12 max-w-6xl shadow-2xl rounded-xl bg-white overflow-hidden mb-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold">Coaching Session Details</h3>
                  <p className="text-blue-100 mt-1">Complete session information and scoring</p>
                </div>
                <button
                  onClick={() => {
                    setIsCoachingModalOpen(false);
                    setSelectedCoachingSession(null);
                  }}
                  className="text-white hover:text-blue-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-8">
              <div className="space-y-8">
                {/* Session Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h4 className="text-sm font-semibold text-blue-800 ml-3">Workshop</h4>
                    </div>
                    <p className="text-gray-800 font-medium">
                      {workshop?.title || `Workshop ${selectedCoachingSession.workshop}`}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h4 className="text-sm font-semibold text-green-800 ml-3">Coach</h4>
                    </div>
                    <p className="text-gray-800 font-medium">
                      {selectedCoachingSession.coach_details?.full_name || selectedCoachingSession.coach_name || mentorName || `User ${selectedCoachingSession.coach_mentor}`}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-sm font-semibold text-purple-800 ml-3">Date</h4>
                    </div>
                    <p className="text-gray-800 font-medium">
                      {selectedCoachingSession.coaching_date ? new Date(selectedCoachingSession.coaching_date).toLocaleDateString() : 'Not scheduled'}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-sm font-semibold text-orange-800 ml-3">Duration</h4>
                    </div>
                    <p className="text-gray-800 font-medium">
                      {selectedCoachingSession.coaching_lead_time_minutes ? `${selectedCoachingSession.coaching_lead_time_minutes} min` : 'Not specified'}
                    </p>
                  </div>
                </div>
                
                {/* Mentees Section */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                      <Users className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Mentees</h4>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {selectedCoachingSession.mentees_details && selectedCoachingSession.mentees_details.length > 0 ? (
                      selectedCoachingSession.mentees_details.map((mentee: any, index: number) => (
                        <div key={index} className="flex items-center bg-white rounded-lg px-4 py-3 shadow-sm border border-indigo-200">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-indigo-600 font-semibold text-sm">
                              {mentee.full_name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <span className="text-gray-800 font-medium">{mentee.full_name}</span>
                        </div>
                      ))
                    ) : selectedCoachingSession.mentees_names && selectedCoachingSession.mentees_names.length > 0 ? (
                      selectedCoachingSession.mentees_names.map((name: string, index: number) => (
                        <div key={index} className="flex items-center bg-white rounded-lg px-4 py-3 shadow-sm border border-indigo-200">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-indigo-600 font-semibold text-sm">
                              {name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <span className="text-gray-800 font-medium">{name}</span>
                        </div>
                      ))
                    ) : menteeNames && menteeNames.length > 0 ? (
                      menteeNames.map((name: string, index: number) => (
                        <div key={index} className="flex items-center bg-white rounded-lg px-4 py-3 shadow-sm border border-indigo-200">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-indigo-600 font-semibold text-sm">
                              {name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <span className="text-gray-800 font-medium">{name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic bg-white rounded-lg px-4 py-3 border border-gray-200">
                        No mentees assigned
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Score Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Performance Summary</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {(() => {
                      const totalScore = selectedCoachingSession.total_score || 0;
                      const scorePercentage = (selectedCoachingSession.total_score_percentage || 0) * 100;
                      const completionRate = selectedCoachingSession.completion_rate || 0;
                      
                      // Performance thresholds
                      const getScoreColor = (score: number) => {
                        if (score >= 35) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' }; // 70%+ (35/50)
                        if (score >= 20) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' }; // 40-69% (20-34/50)
                        return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' }; // <40% (<20/50)
                      };
                      
                      const getPercentageColor = (percentage: number) => {
                        if (percentage >= 70) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' };
                        if (percentage >= 40) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' };
                        return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' };
                      };
                      
                      const getCompletionColor = (rate: number) => {
                        if (rate >= 80) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' };
                        if (rate >= 50) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' };
                        return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' };
                      };
                      
                      const scoreColors = getScoreColor(totalScore);
                      const percentageColors = getPercentageColor(scorePercentage);
                      const completionColors = getCompletionColor(completionRate);
                      
                      return (
                        <>
                          <div className={`bg-white rounded-lg p-6 shadow-sm border ${scoreColors.border} text-center`}>
                            <div className={`text-4xl font-bold ${scoreColors.color} mb-2`}>{totalScore}/50</div>
                            <div className={`text-sm ${scoreColors.color} font-medium`}>Total Score</div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                              <div 
                                className={`${scoreColors.bg} h-2 rounded-full`} 
                                style={{ width: `${(totalScore / 50) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className={`bg-white rounded-lg p-6 shadow-sm border ${percentageColors.border} text-center`}>
                            <div className={`text-4xl font-bold ${percentageColors.color} mb-2`}>{scorePercentage.toFixed(1)}%</div>
                            <div className={`text-sm ${percentageColors.color} font-medium`}>Score Percentage</div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                              <div 
                                className={`${percentageColors.bg} h-2 rounded-full`} 
                                style={{ width: `${scorePercentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className={`bg-white rounded-lg p-6 shadow-sm border ${completionColors.border} text-center`}>
                            <div className={`text-4xl font-bold ${completionColors.color} mb-2`}>{completionRate.toFixed(1)}%</div>
                            <div className={`text-sm ${completionColors.color} font-medium`}>Completion Rate</div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                              <div 
                                className={`${completionColors.bg} h-2 rounded-full`} 
                                style={{ width: `${completionRate}%` }}
                              ></div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Questions & Scores */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Questions & Scores</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {QUESTIONS.map((question, index) => {
                      const score = selectedCoachingSession[`question_${index + 1}_score` as keyof KataCoachingSession] as number;
                      return (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm text-gray-700 font-medium flex-1 pr-4">{question}</span>
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                score !== undefined && score !== null 
                                  ? score >= 4 ? 'bg-green-100 text-green-800 border border-green-200' 
                                  : score >= 2 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                  : 'bg-red-100 text-red-800 border border-red-200'
                                  : 'bg-gray-100 text-gray-500 border border-gray-200'
                              }`}>
                                {score !== undefined && score !== null ? score : 'N/A'}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                score !== undefined && score !== null 
                                  ? score >= 4 ? 'bg-green-500' 
                                  : score >= 2 ? 'bg-yellow-500'
                                  : 'bg-red-500'
                                  : 'bg-gray-300'
                              }`}
                              style={{ width: score !== undefined && score !== null ? `${(score / 5) * 100}%` : '0%' }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Notes Section */}
                {selectedCoachingSession.notes && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">Session Notes</h4>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedCoachingSession.notes}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setIsCoachingModalOpen(false);
                    setSelectedCoachingSession(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttTab;
